import React, { useEffect, useRef } from "react"
import L from "leaflet"
import "leaflet/dist/leaflet.css"
import { COLORS } from "../../utilities/Constants"

// Marqueur dessiné (divIcon) : pas les PNG par défaut de Leaflet, dont les
// URL cassent sous webpack. Pastille ronde à la couleur de la légende,
// numérotée pour les livraisons.
const icone = ({ couleur, numero }) =>
	L.divIcon({
		className: "",
		iconSize: [22, 22],
		iconAnchor: [11, 11],
		html: `<span style="display:flex;align-items:center;justify-content:center;width:22px;height:22px;border-radius:50%;background:${couleur};border:2px solid #fff;box-shadow:0 1px 3px rgba(0,0,0,0.35);box-sizing:border-box;color:#fff;font:600 11px/1 sans-serif">${numero || ""}</span>`,
	})

const ZOOM_POINT_UNIQUE = 16

/**
 * Carte Leaflet de la carte « Trajet » (P9) : un marqueur par point
 * {lat, lng, couleur, numero?, titre}, cadrée sur l'ensemble. Aucun trait
 * entre les points (décision Jordan 06.10 : le vol d'oiseau trompe).
 * Instance gardée en ref : les rendus de la fiche ne la recréent pas, seuls
 * les marqueurs sont refaits quand les points changent.
 */
const CarteTrajetV2 = ({ points, style }) => {
	const conteneurRef = useRef(null)
	const carteRef = useRef(null)
	const marqueursRef = useRef(null)

	useEffect(() => {
		const carte = L.map(conteneurRef.current, {
			scrollWheelZoom: false,
		})
		L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
			maxZoom: 19,
			attribution:
				'&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>',
		}).addTo(carte)
		marqueursRef.current = L.layerGroup().addTo(carte)
		carteRef.current = carte
		// Taille du conteneur fixée par la grille après le montage (et au
		// redimensionnement) : Leaflet doit la relire.
		let observateur = null
		if (typeof ResizeObserver !== "undefined") {
			observateur = new ResizeObserver(() => carte.invalidateSize())
			observateur.observe(conteneurRef.current)
		}
		return () => {
			if (observateur) observateur.disconnect()
			carte.remove()
			carteRef.current = null
		}
	}, [])

	// Clé stable des points : l'effet ne tourne que si un point change.
	const cle = points
		.map(
			(p) =>
				`${p.lat},${p.lng},${p.couleur},${p.numero || ""},${p.titre}`,
		)
		.join("|")
	useEffect(() => {
		const carte = carteRef.current
		const groupe = marqueursRef.current
		if (!carte || !groupe) return
		groupe.clearLayers()
		points.forEach((p) => {
			L.marker([p.lat, p.lng], { icon: icone(p), title: p.titre })
				.bindTooltip(p.titre)
				.addTo(groupe)
		})
		carte.invalidateSize()
		if (points.length === 1) {
			carte.setView([points[0].lat, points[0].lng], ZOOM_POINT_UNIQUE)
		} else if (points.length > 1) {
			carte.fitBounds(
				points.map((p) => [p.lat, p.lng]),
				{ padding: [36, 36], maxZoom: ZOOM_POINT_UNIQUE },
			)
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [cle])

	return (
		<div
			ref={conteneurRef}
			style={{
				width: "100%",
				height: "100%",
				minHeight: 280,
				borderRadius: 12,
				border: `1px solid ${COLORS.border}`,
				// Les panneaux Leaflet ont des z-index élevés : on les confine
				// pour qu'ils ne passent pas devant le footer fixe / les modals.
				position: "relative",
				zIndex: 0,
				...style,
			}}
		/>
	)
}

export default CarteTrajetV2
